import { JSONRenderer } from 'components/json-renderer';
import {
  ActionableNotification,
  Button,
  InlineLoading,
  InlineNotification,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Link,
  Tile,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  Modal,
  TextInput,
  FlexGrid as Grid,
  Row,
  Column,
  Dropdown,
  ComposedModal,
  ModalHeader,
  ModalBody,
} from '@carbon/react';
import { Provider, useAtom, useAtomValue, useStore, useSetAtom } from 'jotai';
import {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { Date } from 'components/date';
import { format, parseISO } from 'date-fns';

export interface ProvisionsHeaderSlots {
  title: HTMLElement | null;
  actions: HTMLElement | null;
}

export const ProvisionsHeaderSlotsContext =
  createContext<ProvisionsHeaderSlots>({ title: null, actions: null });
import {
  getFormDefaults,
  getPayload,
  provisionAtom,
  provisionHrefAtom,
  provisionListAtom,
  provisionsAtom,
  provisionsHrefAtom,
  useCompletionItems,
  useConfigurationOptionsAtom,
  useOCRExtractionAtom,
  refreshProvisionCounter,
  isCompositeProvision,
  currentProvisionIdAtom,
  refreshProvisionsCounter,
} from './atoms';
import {
  PROVISION_FAILED,
  subscribeToChannel,
  useProvisionDocument,
  useProvisionListener,
} from 'lib/hooks/useProvisionDocument';
import {
  CompletionConfigurationOption,
  CompletionConfigurationResponse,
  CompletionPromptConfigurationOption,
  DocumentResponse,
  OCRExtractionResponse,
  ProvisionResponse,
} from 'types/api-responses';
import { JotaiStore, JSONObject, JSONValue } from 'lib/types';
import { SuccessToast } from 'components/success-toast';
import { StandingErrorToast } from 'components/error-toast';
import { EmptyState } from 'components/empty-state';
import { Default } from 'components/icons';
import {
  CaretDown,
  Checkmark,
  CaretRight,
  CaretLeft,
  Information,
  SysProvision,
  CalendarSettings,
  DocumentPdf,
  Restart,
} from '@carbon/react/icons';
import styles from '../json-renderer/json-renderer.module.scss';
import { useComponentVisible } from 'lib/hooks/useComponentVisible';
import { useEscapeToClose } from 'lib/hooks/useEscapeToClose';
import { useLatest } from 'lib/hooks/use-latest';
import { isImageInlineRenderable } from 'utils/document-image';
import { EventsPanel, EventsPanelOpenButton } from 'components/events-panel';
import { DebouncedInput } from 'components/debounced-input';
import classNames from 'classnames';
import { useDownloadResource } from 'lib/hooks/useDownloadFile';
import { useBodyClass } from 'lib/hooks/useBodyClass';
import { DownloadButton } from 'components/download-button';
import { CopyButton } from 'components/copy-button/copy-button';
import { Pagination } from 'components/pagination';
import { createMutationAtom } from 'atoms/create-resource-atom';

import documentStyles from './provisioned-document.module.scss';
import { updateNestedValue } from 'components/json-renderer/utils';

const reapplyDocumentAtom = createMutationAtom<
  null,
  'autoProvisionedDocumentProcessJob'
>();

const cancelProvisionAtom = createMutationAtom<any, 'cancelProvision'>();

export function List({
  store,
  onProvisionsAvailable,
  document,
}: {
  store: JotaiStore;
  onProvisionsAvailable: () => void;
  document?: DocumentResponse;
}) {
  return (
    <Provider store={store}>
      <FetchListChecker
        onProvisionsAvailable={onProvisionsAvailable}
        document={document}
      />
    </Provider>
  );
}

export function AvailableProvisions({
  href,
  configurationHref,
  onProvisionsAvailable,
  store,
  hasImage,
}: {
  href: string;
  hasImage: boolean;
  configurationHref: string;
  onProvisionsAvailable: () => void;
  store: JotaiStore;
}) {
  return (
    <Provider store={store}>
      <Provisions
        key={href}
        href={href}
        configurationHref={configurationHref}
        onProvisionsAvailable={onProvisionsAvailable}
        hasImage={hasImage}
      />
    </Provider>
  );
}

function FetchListChecker({
  onProvisionsAvailable,
  document,
}: {
  onProvisionsAvailable: () => void;
  document?: DocumentResponse;
}) {
  const provisions = useAtomValue(provisionsAtom);
  const newProvisionList = useAtomValue(provisionListAtom);

  if (provisions.state === 'loading')
    return <InlineLoading description="Loading provisions..." />;

  const list = newProvisionList.length
    ? newProvisionList
    : provisions.state === 'hasData' &&
        provisions.data &&
        !('error' in provisions.data)
      ? provisions.data
      : [];

  if (!list.length && provisions.state === 'hasData')
    return (
      <Tile className={styles.emptyStateCol}>
        <EmptyState
          IconComponent={Default}
          headerText="No Provisions Found"
          helperText="No Provisions Generated Yet."
        />
      </Tile>
    );

  return (
    <FetchSingleProvision
      key={list.length}
      provisions={list}
      onProvisionsAvailable={onProvisionsAvailable}
      document={document}
    />
  );
}

function ViewDocumentModal({
  isOpen,
  onClose,
  currentProvision,
  provisions,
  setProvision,
  onProvisionsAvailable,
  document,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentProvision: ProvisionResponse;
  provisions: ProvisionResponse[];
  setProvision: React.Dispatch<React.SetStateAction<ProvisionResponse>>;
  onProvisionsAvailable: () => void;
  document?: DocumentResponse;
}) {
  // FetchSingleProvision has pointed the store at currentProvision.
  const provisionReq = useAtomValue(provisionAtom);

  if (provisionReq.state === 'loading') {
    return <InlineLoading description="Loading provisions..." />;
  }

  if (provisionReq.state === 'hasError') {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        title="Error loading provisions"
      />
    );
  }

  if (
    provisionReq.state === 'hasData' &&
    provisionReq.data &&
    !('error' in provisionReq.data)
  ) {
    const image = document?.resources?.image;
    const imageInlineRenderable = isImageInlineRenderable(image?.contentType);

    return (
      <ComposedModal open={isOpen} onClose={onClose} size="lg">
        <ModalHeader title="View Document" />
        <ModalBody className={documentStyles.modalBody}>
          <div className={documentStyles.modalContentSplit}>
            <div className={documentStyles.leftPane}>
              {/* Left side: Provisions */}
              <ProvisionSwitcher
                currentProvision={currentProvision}
                provisions={provisions}
                setProvision={setProvision}
                onProvisionsAvailable={onProvisionsAvailable}
              />
              <ProvisionTabs
                provision={provisionReq.data}
                onPaginateParagraphs={() => {}}
              />
            </div>
            <div className={documentStyles.rightPane}>
              {/* Right side: Document iframe (or Download for non-renderable types) */}
              {image &&
                (imageInlineRenderable ? (
                  <iframe
                    src={image.href}
                    title="Document Viewer"
                    className={documentStyles.documentIframe}
                  />
                ) : (
                  <div className={documentStyles.downloadOnly}>
                    <p>{image.filename ?? 'Document file'}</p>
                    <Button
                      kind="primary"
                      href={image.href}
                      target="_blank"
                      rel="noopener"
                    >
                      Download
                    </Button>
                  </div>
                ))}
            </div>
          </div>
        </ModalBody>
      </ComposedModal>
    );
  }

  return null;
}

function ViewDocumentButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      hasIconOnly
      kind="tertiary"
      size="md"
      renderIcon={DocumentPdf}
      iconDescription="View Document"
      tooltipAlignment="center"
      tooltipPosition="bottom"
      onClick={onClick}
    />
  );
}

/**
 * Points the shared store's provision atom at one provision, and reads it.
 * Until the store holds that selection, it may hold the last one, so the
 * provision reads as loading. The selection is cleared on unmount.
 * @param {string} href - The provision's href.
 * @param {number | string} id - The provision's id.
 * @return {object} The provision atom's loadable.
 */
function useSelectedProvision(href: string, id: ProvisionResponse['id']) {
  const store = useStore();
  useLayoutEffect(() => {
    store.set(provisionHrefAtom, href);
    store.set(currentProvisionIdAtom, id);
    return () => {
      store.set(provisionHrefAtom, undefined);
      store.set(currentProvisionIdAtom, undefined);
    };
  }, [store, href, id]);
  const storeHref = useAtomValue(provisionHrefAtom);
  const storeId = useAtomValue(currentProvisionIdAtom);
  const provisionReq = useAtomValue(provisionAtom);
  return storeHref === href && storeId === id
    ? provisionReq
    : { state: 'loading' as const };
}

function FetchSingleProvision({
  provisions,
  onProvisionsAvailable,
  document,
}: {
  provisions: ProvisionResponse[];
  onProvisionsAvailable: () => void;
  document?: DocumentResponse;
}) {
  const { title: titleSlot, actions: actionsSlot } = useContext(
    ProvisionsHeaderSlotsContext
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProvision, setCurrentProvision] = useState(provisions[0]);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const provisionHref = currentProvision.href;
  const provisionReq = useSelectedProvision(provisionHref, currentProvision.id);
  const provisionsHref = useAtomValue(provisionsHrefAtom);
  const setProvisionList = useSetAtom(provisionListAtom);
  const { triggerProvision, generatingProvision } =
    useProvisionDocument(provisionsHref);
  const shouldShowProvisionBanner =
    !generatingProvision &&
    !provisions.some(isCompositeProvision) &&
    provisionReq.state === 'hasData' &&
    provisionReq.data &&
    !('error' in provisionReq.data) &&
    !(provisionReq.data?.status === 'queued') &&
    !(provisionReq.data?.status === 'pending');

  const outputRef = useRef<HTMLDivElement | null>(null);
  const onPaginateParagraphs = () => {
    if (outputRef.current) {
      outputRef.current.scrollTo(0, 0);
    }
  };

  const refreshProvision = useSetAtom(refreshProvisionCounter);
  const refreshProvisions = useSetAtom(refreshProvisionsCounter);

  const provisionData =
    provisionReq.state === 'hasData' ? provisionReq.data : null;
  const hasValidData = provisionData && !('error' in provisionData);
  const isProcessing =
    hasValidData &&
    (provisionData.status === 'pending' || provisionData.status === 'queued');
  // As in Provisions: keyed on strings, with the callback read through a ref,
  // so a parent's new callback each render doesn't re-subscribe.
  const onProvisionsAvailableRef = useLatest(onProvisionsAvailable);
  const processingChannel = currentProvision.notifications.channel;
  const processingEvent = currentProvision.notifications.events[0].name;

  useEffect(() => {
    if (!isProcessing) return;

    return subscribeToChannel(
      processingChannel,
      processingEvent,
      provisionHref,
      () => {
        refreshProvisions();
        refreshProvision();
        onProvisionsAvailableRef.current();
      },
      () => {
        // Surface provisioning errors to the user
        console.error('Provision processing failed for:', provisionHref);
      }
    );
  }, [
    isProcessing,
    provisionHref,
    processingChannel,
    processingEvent,
    onProvisionsAvailableRef,
    refreshProvision,
    refreshProvisions,
  ]);

  if (provisionReq.state === 'loading')
    return <InlineLoading description="Loading provisions..." />;
  if (provisionReq.state === 'hasError')
    return (
      <InlineNotification
        kind="error"
        lowContrast
        title="Error loading provisions"
      />
    );

  if (!hasValidData) return null;

  const actionButtonsNode = !isProcessing ? (
    <>
      <ViewDocumentButton onClick={() => setIsModalOpen(true)} />
      <EventsPanelOpenButton tooltipPosition="bottom" tooltipAlignment="end" />
      <MetadataModal
        metadata={currentProvision.metadata}
        createdAt={currentProvision.createdAt}
      />
    </>
  ) : null;

  const switcherNode = (
    <ProvisionSwitcher
      currentProvision={currentProvision}
      provisions={provisions}
      setProvision={setCurrentProvision}
      onProvisionsAvailable={onProvisionsAvailable}
    />
  );

  const renderInlineTitleRow =
    !titleSlot || (!actionsSlot && !!actionButtonsNode);

  return (
    <div className={styles.provisionsPanel} ref={outputRef}>
      {bannerError && (
        <StandingErrorToast
          message={bannerError}
          onClose={() => setBannerError(null)}
        />
      )}
      {!isProcessing && (
        <ViewDocumentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          currentProvision={currentProvision}
          provisions={provisions}
          setProvision={setCurrentProvision}
          onProvisionsAvailable={onProvisionsAvailable}
          document={document}
        />
      )}
      {titleSlot ? createPortal(switcherNode, titleSlot) : null}
      {actionsSlot && actionButtonsNode
        ? createPortal(actionButtonsNode, actionsSlot)
        : null}
      {renderInlineTitleRow && (
        <div className={styles.provisionsTitle}>
          {!titleSlot && switcherNode}
          {!actionsSlot && actionButtonsNode && <div>{actionButtonsNode}</div>}
        </div>
      )}
      {isProcessing ? (
        <div className={styles.processingState}>
          <InlineLoading
            description={`Provisioning is ${provisionData.status}...`}
          />
        </div>
      ) : (
        <>
          {shouldShowProvisionBanner && (
            <div className={styles.provisionsBanner}>
              <ActionableNotification
                inline
                kind="info"
                lowContrast
                hideCloseButton={true}
                title="Please re-run the provisioner to access new features"
                actionButtonLabel={
                  generatingProvision ? 'Provisioning...' : 'Provision'
                }
                onActionButtonClick={() => {
                  if (!generatingProvision) {
                    setBannerError(null);
                    triggerProvision({
                      // Hands the run to the header, which hears it on a reload from
                      // the server (a local list would hide it); this list unmounts.
                      onQueued: () => {
                        setProvisionList([]);
                        refreshProvisions();
                      },
                      onError: setBannerError,
                    });
                  }
                }}
              />
            </div>
          )}
          <div className={styles.provisionsContent}>
            <EventsContent provision={provisionData} />
            <ProvisionTabs
              provision={provisionData}
              onPaginateParagraphs={onPaginateParagraphs}
            />
          </div>
        </>
      )}
    </div>
  );
}

function Provisions({
  href,
  configurationHref,
  onProvisionsAvailable,
  hasImage,
}: {
  href: string;
  configurationHref: string;
  onProvisionsAvailable: () => void;
  hasImage: boolean;
}) {
  const store = useStore();
  // Points the shared store at this document. The cleanup is a layout one too,
  // so the last document's cleanup comes before this document's setup.
  useLayoutEffect(() => {
    store.set(provisionsHrefAtom, href);
    return () => {
      store.set(provisionListAtom, []);
      store.set(provisionsHrefAtom, undefined);
    };
  }, [store, href]);
  // Until then, the store still holds the last document's provisions.
  const storeReady = useAtomValue(provisionsHrefAtom) === href;

  const provisions = useAtomValue(provisionsAtom);
  const [currentProvisions] = useAtom(provisionListAtom);
  const toasts = useProvisionToasts();
  const { setShowSuccess, setProvisionError } = toasts;
  const refreshProvision = useSetAtom(refreshProvisionCounter);
  const refreshProvisions = useSetAtom(refreshProvisionsCounter);
  const [, cancelProvision] = useAtom(cancelProvisionAtom);
  // Read through a ref, so a parent's new callback each render doesn't re-subscribe.
  const onProvisionsAvailableRef = useLatest(onProvisionsAvailable);

  const provisionsList = currentProvisions.length
    ? currentProvisions
    : provisions.state === 'hasData' &&
        provisions.data &&
        !('error' in provisions.data)
      ? provisions.data
      : [];

  const lastProvision = provisionsList.length ? provisionsList[0] : undefined;
  const pendingProvision =
    storeReady &&
    provisions.state === 'hasData' &&
    (lastProvision?.status === 'pending' || lastProvision?.status === 'queued')
      ? lastProvision
      : undefined;
  // Keyed on strings, so a refetched copy of the same provision keeps its subscription.
  const pendingHref = pendingProvision?.href;
  const pendingChannel = pendingProvision?.notifications.channel;
  const pendingEvent = pendingProvision?.notifications.events[0].name;

  useEffect(() => {
    if (!pendingHref || !pendingChannel || !pendingEvent) return;

    return subscribeToChannel(
      pendingChannel,
      pendingEvent,
      pendingHref,
      () => {
        // Refresh so that we force the atom to refresh from server even though href has not changed
        refreshProvisions();
        refreshProvision();
        setShowSuccess(true);
        onProvisionsAvailableRef.current();
      },
      () => {
        // Reloads too, so the failed run stops reading as pending.
        refreshProvisions();
        refreshProvision();
        setProvisionError(PROVISION_FAILED);
      }
    );
  }, [
    pendingHref,
    pendingChannel,
    pendingEvent,
    onProvisionsAvailableRef,
    refreshProvision,
    refreshProvisions,
    setShowSuccess,
    setProvisionError,
  ]);

  /**
   * The header under its toasts, by state.
   * @return {JSX.Element} The loading, error, pending or main content.
   */
  const renderContent = () => {
    if (!storeReady || provisions.state === 'loading')
      return <InlineLoading description="Loading provisions..." />;
    if (provisions.state === 'hasError')
      return (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error loading provisions"
        />
      );

    if (pendingProvision) {
      return (
        <>
          <InlineLoading
            description={`Provisioning is ${pendingProvision.status}`}
          />
          <Button
            kind="danger"
            size="sm"
            onClick={() => {
              cancelProvision({
                type: 'PATCH',
                url: pendingProvision.markAsCancelledHref,
                data: null,
                onSuccess: () => {
                  refreshProvisions();
                  refreshProvision();
                },
              });
            }}
          >
            <span>Cancel Provision</span>
          </Button>
        </>
      );
    }
    const disabledLabel = !hasImage ? (
      <>
        Provision Now unavailable.
        <br />
        Document image is missing.
      </>
    ) : (
      'Provisioning completed. Only 1 provision per document.'
    );

    return (lastProvision && lastProvision.status === 'complete') ||
      !hasImage ? (
      <>
        <Tooltip label={disabledLabel} align="bottom">
          <span>
            <Button kind="primary" renderIcon={SysProvision} disabled size="sm">
              <span>Provision Now</span>
            </Button>
          </span>
        </Tooltip>
        <ConfigureModal
          href={href}
          configurationHref={configurationHref}
          onProvisionsAvailable={onProvisionsAvailable}
          toasts={toasts}
        />
      </>
    ) : (
      <>
        <ConfigureModal
          href={href}
          configurationHref={configurationHref}
          onProvisionsAvailable={onProvisionsAvailable}
          toasts={toasts}
        />
        <Trigger
          href={href}
          onProvisionsAvailable={onProvisionsAvailable}
          toasts={toasts}
        />
      </>
    );
  };

  // Mounted once above every state, so a reload neither hides the toasts nor restarts their timers.
  return (
    <>
      <ProvisionToasts toasts={toasts} />
      {renderContent()}
    </>
  );
}

function Trigger({
  href,
  onProvisionsAvailable,
  toasts,
}: {
  href: string;
  onProvisionsAvailable: () => void;
  toasts: ProvisionToastsState;
}) {
  const { triggerProvision, generatingProvision } = useProvisionDocument(href);
  const [provisions, setProvisionList] = useAtom(provisionListAtom);
  return !generatingProvision ? (
    <>
      <Button
        disabled={!!provisions.length}
        kind="primary"
        size="sm"
        renderIcon={SysProvision}
        onClick={() => {
          toasts.reset();
          triggerProvision({
            onGeneratingProvision: (provision) => {
              setProvisionList([provision, ...provisions]);
              onProvisionsAvailable();
              toasts.setShowSuccess(true);
            },
            onError: toasts.setProvisionError,
          });
        }}
      >
        <span>Provision Now</span>
      </Button>
    </>
  ) : (
    <InlineLoading description={'Provisioning document...'} />
  );
}

function Viewer({
  provision: initialProvision,
}: {
  provision: ProvisionResponse;
}) {
  const itemHref = initialProvision.resources.items.href;
  const itemsAtom = useCompletionItems(itemHref);
  const items = useAtomValue(itemsAtom);
  const refreshProvisions = useSetAtom(refreshProvisionsCounter);
  const [editableProvisionContent, setEditableProvisionContent] =
    useState<JSONObject | null>(null);

  useEffect(() => {
    const content = initialProvision.content;
    if (
      typeof content === 'object' &&
      content !== null &&
      !Array.isArray(content)
    ) {
      setEditableProvisionContent(content);
    } else {
      setEditableProvisionContent({});
    }
  }, [initialProvision.content]);

  const handleLocalDataUpdate = useCallback(
    (path: string[], newValue: JSONValue): void => {
      setEditableProvisionContent((prevContent) =>
        updateNestedValue(prevContent, path, newValue)
      );
    },
    []
  );

  if (items.loading) return <InlineLoading />;
  if (items.data && 'error' in items.data)
    return (
      <InlineNotification kind="error" title={items.data.error} lowContrast />
    );

  return (
    <>
      <JSONRenderer
        data={editableProvisionContent || {}}
        completionHref={initialProvision.href}
        itemsHref={initialProvision.resources.items.href}
        items={items.data || []}
        state={initialProvision.state}
        documentState={initialProvision.documentState}
        refreshProvisions={refreshProvisions}
        isCompositeProvision={isCompositeProvision(initialProvision)}
        onDataUpdate={handleLocalDataUpdate}
      />
    </>
  );
}

function ProvisionTabs({
  provision,
  onPaginateParagraphs,
}: {
  provision: ProvisionResponse;
  onPaginateParagraphs: () => void;
}) {
  return (
    <Tabs>
      <TabList contained>
        <Tab>Provisions</Tab>
        <Tab>OCR</Tab>
      </TabList>
      <TabPanels>
        <TabPanel>
          <Viewer provision={provision} />
        </TabPanel>
        <TabPanel>
          <OCRFetcher
            href={provision.resources.extraction.href}
            onPaginateParagraphs={onPaginateParagraphs}
          />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
}

function ProvisionSwitcher({
  currentProvision,
  provisions,
  setProvision,
  onProvisionsAvailable,
}: {
  currentProvision: ProvisionResponse;
  provisions: ProvisionResponse[];
  setProvision: React.Dispatch<React.SetStateAction<ProvisionResponse>>;
  onProvisionsAvailable: () => void;
}) {
  const [loadingReapplyDocument, setLoadingReapplyDocument] = useState(false);
  const [, reapplyDocument] = useAtom(reapplyDocumentAtom);
  const listen = useProvisionListener();
  const { ref, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);
  const panelId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const closeOnEscape = useCallback(() => {
    // Back on the toggle only from inside the switcher; focus elsewhere stays put.
    const hadFocus = ref.current?.contains(document.activeElement);
    setIsComponentVisible(false);
    if (hadFocus) toggleRef.current?.focus();
  }, [ref, setIsComponentVisible]);
  useEscapeToClose(isComponentVisible, closeOnEscape);

  const index = provisions.findIndex((el) => el.id === currentProvision.id);
  const mostRecentDocumentExtractionCompletionId =
    provisions.length > 1 && provisions[1].id;
  const selectedIndex = provisions.length - index;
  const showRestructureButton =
    currentProvision &&
    currentProvision.status === 'complete' &&
    isCompositeProvision(currentProvision);

  return (
    <div className={documentStyles.provisionsSwitcher}>
      <h2>
        {isCompositeProvision(currentProvision)
          ? 'Provisions'
          : `LLM Output ${selectedIndex}`}
      </h2>
      <div ref={ref}>
        <Button
          ref={toggleRef}
          hasIconOnly
          kind="ghost"
          size="sm"
          renderIcon={CaretDown}
          iconDescription="Switch provision"
          tooltipPosition="bottom"
          aria-expanded={isComponentVisible}
          aria-controls={panelId}
          onClick={() => setIsComponentVisible((current) => !current)}
        />

        {isComponentVisible && (
          <div className={documentStyles.dropdownPanel}>
            <ul id={panelId}>
              {provisions.map((el, i) => {
                const isComposite = isCompositeProvision(el);
                return (
                  <li key={el.id}>
                    <Link
                      as="button"
                      type="button"
                      className={documentStyles.provisionLinks}
                      aria-current={currentProvision.id === el.id || undefined}
                      onClick={() => {
                        setProvision(el);
                        setIsComponentVisible(false);
                      }}
                    >
                      {currentProvision.id === el.id && <Checkmark />}
                      {isComposite ? (
                        'Provisions'
                      ) : (
                        <>
                          {`LLM Output ${provisions.length - i} - `}
                          <Date
                            date={el.createdAt}
                            format="M/DD/YYYY h:mm A"
                            showTime
                          />
                        </>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
      <div>
        {showRestructureButton && (
          <Button
            hasIconOnly={true}
            renderIcon={Restart}
            iconDescription="Restructure Data"
            tooltipAlignment="center"
            tooltipPosition="bottom"
            size="sm"
            kind="tertiary"
            disabled={loadingReapplyDocument}
            onClick={() => {
              setLoadingReapplyDocument(true);
              const documentExtractionCompletionId = isCompositeProvision(
                currentProvision
              )
                ? mostRecentDocumentExtractionCompletionId
                : currentProvision.id;
              reapplyDocument({
                type: 'POST',
                url: `/document_extraction_completions/${documentExtractionCompletionId}/auto_provisioned_document_process_jobs`,
                data: null,
                onSuccess: () => {
                  listen(
                    currentProvision.notifications.channel,
                    currentProvision.notifications.events[0].name,
                    currentProvision.href,
                    () => {
                      onProvisionsAvailable();
                      setLoadingReapplyDocument(false);
                    },
                    () => {
                      setLoadingReapplyDocument(false);
                    }
                  );
                },
                onError: (error) => {
                  console.error('Error:', error);
                  setLoadingReapplyDocument(false);
                },
              });
            }}
          />
        )}
      </div>
    </div>
  );
}

function OCRFetcher({
  href,
  onPaginateParagraphs,
}: {
  href: string;
  onPaginateParagraphs: () => void;
}) {
  const extractionAtom = useOCRExtractionAtom(href);
  const extraction = useAtomValue(extractionAtom);
  if (extraction.loading) return <InlineLoading />;
  if (extraction.data && 'error' in extraction.data)
    return (
      <InlineNotification
        kind="error"
        title={extraction.data.error}
        lowContrast
      />
    );

  const data = extraction.data;

  return data ? (
    <OCROutput output={data} onPaginateParagraphs={onPaginateParagraphs} />
  ) : null;
}

function OCROutput({
  output,
  onPaginateParagraphs,
}: {
  output: OCRExtractionResponse;
  onPaginateParagraphs: () => void;
}) {
  const paragraphs = output.content.paragraphs;
  const tables = output.content.tables;
  const text = output.content.text;
  const [view, setView] = useState<'paragraphs' | 'tables' | 'text'>(
    paragraphs ? 'paragraphs' : 'text'
  );
  const download = useDownloadResource(output.resources.contentDownload.href);
  return (
    <div className={styles.ocrOutputPanel}>
      <div className={styles.ocrButtonsActions}>
        {paragraphs && (
          <button
            className={classNames(
              styles.pillsBtn,
              view === 'paragraphs' ? styles.active : null
            )}
            onClick={() => setView('paragraphs')}
          >
            Text
          </button>
        )}
        {tables && (
          <button
            className={classNames(
              styles.pillsBtn,
              view === 'tables' ? styles.active : null
            )}
            onClick={() => setView('tables')}
          >
            Tables
          </button>
        )}
        <div className={styles.download}>
          <DownloadButton
            hasIconOnly
            kind="tertiary"
            iconDescription="Download OCR Result"
            tooltipAlignment="end"
            tooltipPosition="bottom"
            loading={download.generatingDownload}
            loadingText="Downloading"
            onClick={() => download.triggerDownload()}
          ></DownloadButton>
        </div>
      </div>
      {view === 'paragraphs' && paragraphs && (
        <OCRParagraphs
          paragraphs={paragraphs}
          onPaginateParagraphs={onPaginateParagraphs}
        />
      )}
      {view === 'tables' && tables && <OCRTables tables={tables} />}
      {view === 'text' && text && <TextOutput text={text} />}
    </div>
  );
}

function OCRParagraphs({
  paragraphs,
  onPaginateParagraphs,
}: {
  paragraphs: NonNullable<OCRExtractionResponse['content']['paragraphs']>;
  onPaginateParagraphs: () => void;
}) {
  const [search, setSearch] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 200;

  const filteredParagraphs = searchParagraphs(paragraphs, search);
  const totalPages = Math.ceil(filteredParagraphs.length / itemsPerPage);

  const getCurrentPageParagraphs = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredParagraphs.slice(startIndex, endIndex);
  };

  const currentParagraphs = getCurrentPageParagraphs();

  const pagination = (
    <Pagination
      page={currentPage}
      totalItems={filteredParagraphs.length}
      itemsPerPageText="Items"
      size="md"
      pagesUnknown
      pageSize={itemsPerPage}
      pageSizes={[itemsPerPage]}
      onChange={({ page }) => {
        onPaginateParagraphs();
        setCurrentPage(page);
      }}
    />
  );

  return (
    <div>
      <DebouncedInput
        placeholder="Search in paragraphs..."
        value={search}
        onChange={(value) => {
          if (typeof value === 'string') {
            setSearch(value);
            setCurrentPage(1); // Reset to first page on new search
          }
        }}
      />
      {filteredParagraphs.length === 0 ? (
        <Tile>
          <EmptyState
            headerText="No Results Found"
            helperText="Try adjusting your search to find what you're looking for."
          />
        </Tile>
      ) : (
        <>
          {totalPages > 1 && pagination}
          {currentParagraphs.map((el, i) => (
            <div key={i} className={styles.paragraphsWrapper}>
              <div>
                <h3>{el.role || 'Unknown'}</h3>
                <CopyButton node={el.content}></CopyButton>
              </div>
              <p dangerouslySetInnerHTML={{ __html: el.content }} />
            </div>
          ))}
          {totalPages > 1 && pagination}
        </>
      )}
    </div>
  );
}
type OCRTable = NonNullable<OCRExtractionResponse['content']['tables']>;

function OCRTables({ tables }: { tables: OCRTable }) {
  const [currentTableIndex, setCurrentTableIndex] = useState(0);

  if (!tables.length)
    return (
      <EmptyState
        IconComponent={Default}
        headerText="No Tables Found"
        helperText="There are no tables"
      />
    );

  const table = tables[currentTableIndex];

  const { rowCount, columnCount, cells } = table;

  const tableArray: OCRTable[number]['cells'][] = Array.from(
    { length: rowCount },
    () =>
      Array.from({ length: columnCount }, () => ({
        rowIndex: 0,
        columnIndex: 0,
        content: '',
      }))
  );

  cells.forEach((cell) => {
    tableArray[cell.rowIndex][cell.columnIndex] = cell;
  });

  // Determine if we have a header row
  const hasHeaderRow = tableArray[0].some(
    (cell) => cell.kind === 'columnHeader'
  );

  const rows = hasHeaderRow ? tableArray.slice(1) : tableArray;

  return (
    <>
      <div className={styles.ocrButtonsActions}>
        <legend>
          Table {currentTableIndex + 1} of {tables.length}
        </legend>
        <Button
          hasIconOnly
          kind="ghost"
          size="md"
          iconDescription="Previous Table"
          tooltipAlignment="center"
          tooltipPosition="bottom"
          renderIcon={CaretLeft}
          onClick={() => setCurrentTableIndex(currentTableIndex - 1)}
          disabled={!currentTableIndex}
        />
        <Button
          hasIconOnly
          kind="ghost"
          size="md"
          iconDescription="Next Table"
          tooltipAlignment="center"
          tooltipPosition="bottom"
          renderIcon={CaretRight}
          onClick={() => setCurrentTableIndex(currentTableIndex + 1)}
          disabled={currentTableIndex === tables.length - 1}
        />
      </div>
      <div className={styles.ocrTables}>
        <TableContainer>
          <Table>
            {hasHeaderRow && (
              <TableHead>
                <TableRow>
                  {tableArray[0].map((cell, index) => (
                    <TableHeader key={index}>{cell.content}</TableHeader>
                  ))}
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <TableHeader key={cellIndex}>{cell.content}</TableHeader>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );
}

function TextOutput({
  text,
}: {
  text: NonNullable<OCRExtractionResponse['content']['text']>;
}) {
  return (
    <div className={styles.paragraphsWrapper}>
      <div>
        <h3>Complete OCR</h3>
      </div>
      <p>{text}</p>
    </div>
  );
}

function ConfigureModal({
  href,
  configurationHref,
  onProvisionsAvailable,
  toasts,
}: {
  href: string;
  configurationHref: string;
  onProvisionsAvailable: () => void;
  toasts: ProvisionToastsState;
}) {
  const { triggerProvision, generatingProvision } = useProvisionDocument(href);
  const configurationOptionsAtom =
    useConfigurationOptionsAtom(configurationHref);
  const configuration = useAtomValue(configurationOptionsAtom);
  const [newProvisionList, setProvisionList] = useAtom(provisionListAtom);
  const provisions = useAtomValue(provisionsAtom);

  const currentList = newProvisionList.length
    ? newProvisionList
    : provisions.state === 'hasData' &&
        provisions.data &&
        !('error' in provisions.data)
      ? provisions.data
      : [];

  const triggerProvisionCallback = (payload: ReturnType<typeof getPayload>) => {
    toasts.reset();
    triggerProvision({
      payload,
      onGeneratingProvision: (provision) => {
        setProvisionList([provision, ...currentList]);
        onProvisionsAvailable();
        toasts.setShowSuccess(true);
      },
      onError: toasts.setProvisionError,
    });
  };
  if (configuration.loading || generatingProvision)
    return (
      <div className={styles.loadingProvision}>
        <InlineLoading />
      </div>
    );
  if (configuration.data && 'error' in configuration.data)
    return (
      <InlineNotification
        kind="error"
        title={configuration.data.error}
        lowContrast
      />
    );

  const configurationObject = configuration.data;
  if (!configurationObject) return null;

  return (
    <ConfigureModalImpl
      configurationObject={configurationObject}
      onTriggerProvision={triggerProvisionCallback}
    />
  );
}

function ConfigureModalImpl({
  configurationObject,
  onTriggerProvision,
}: {
  configurationObject: CompletionConfigurationResponse;
  onTriggerProvision: (payload: any) => void;
}) {
  const formDefaults = getFormDefaults(configurationObject);
  const [open, setOpen] = useState(false);
  const [ocr, setOcr] = useState<CompletionConfigurationOption | undefined>(
    formDefaults.extractionConfigurations
  );
  const [prompt, setPrompt] = useState<
    CompletionPromptConfigurationOption | undefined
  >(formDefaults.promptConfigurations);
  const [llm, setLLM] = useState<CompletionConfigurationOption | undefined>(
    formDefaults.completionConfigurations
  );
  useBodyClass('edit-document-modal-open', open);

  return (
    <>
      <Button
        hasIconOnly
        kind="primary"
        size="md"
        renderIcon={CalendarSettings}
        iconDescription={'Configure Provision'}
        tooltipAlignment="center"
        tooltipPosition="bottom"
        onClick={() => setOpen(true)}
      />
      <Modal
        open={open}
        modalHeading="Configure Provision"
        className={documentStyles.configurationModal}
        onRequestClose={() => setOpen(false)}
        onRequestSubmit={() => {
          if (ocr && prompt && llm) {
            onTriggerProvision(
              getPayload({
                extractionConfiguration: ocr,
                promptConfiguration: prompt,
                completionConfiguration: llm,
              })
            );
            setOpen(false);
          }
        }}
        secondaryButtonText="Cancel"
        primaryButtonText="Provision Now"
      >
        <div>
          <fieldset>
            <Dropdown
              titleText={<label>OCR Model</label>}
              selectedItem={ocr}
              onChange={({ selectedItem }) => {
                if (selectedItem) setOcr(selectedItem);
              }}
              itemToString={(item) => {
                return item
                  ? `${item.provider} ${item.version}${
                      item.default ? ' (default)' : ''
                    }`
                  : '';
              }}
              id="ocr-model"
              items={configurationObject.extractionConfigurations}
              label=""
            />
          </fieldset>
          <fieldset>
            <Dropdown
              titleText={<label>Prompt Version</label>}
              helperText={prompt?.category?.replace(/_/g, ' ') ?? ''}
              selectedItem={prompt}
              onChange={({ selectedItem }) => {
                if (selectedItem) setPrompt(selectedItem);
              }}
              itemToString={(item) => {
                return item
                  ? `${item.version}${item.default ? ' (default)' : ''}`
                  : '';
              }}
              id="prompt-version"
              items={configurationObject.promptConfigurations}
              label=""
            />
          </fieldset>
          <fieldset>
            <Dropdown
              titleText={<label>LLM Model</label>}
              selectedItem={llm}
              onChange={({ selectedItem }) => {
                if (selectedItem) setLLM(selectedItem);
              }}
              itemToString={(item) => {
                return item
                  ? `${item.provider} ${item.version}${
                      item.default ? ' (default)' : ''
                    }`
                  : '';
              }}
              id="llm-model"
              items={configurationObject.completionConfigurations}
              label=""
            />
          </fieldset>
        </div>
      </Modal>
    </>
  );
}

function MetadataModal({
  metadata,
  createdAt,
}: {
  metadata: ProvisionResponse['metadata'];
  createdAt: string;
}) {
  const [open, setOpen] = useState(false);
  const createAtFormatted = format(parseISO(createdAt), 'MM/dd/yyyy h:mm aaaa');

  return (
    <>
      <Button
        hasIconOnly
        kind="tertiary"
        size="md"
        renderIcon={Information}
        iconDescription={'Information'}
        tooltipAlignment="end"
        tooltipPosition="bottom"
        onClick={() => setOpen(true)}
      />
      <Modal
        open={open}
        modalHeading="Information"
        passiveModal
        className={styles.informationModal}
        onRequestClose={() => setOpen(false)}
      >
        <Grid className="bx--no-gutter" fullWidth>
          <Row>
            <Column md={4}>
              <TextInput
                value={metadata.completion.createdBy || ''}
                labelText="Owner:"
                id="owner"
                readOnly
              />
            </Column>
            <Column md={4}>
              <TextInput
                value={createAtFormatted || ''}
                labelText="Date Created:"
                id="created"
                readOnly
              />
            </Column>
          </Row>

          <Row>
            <Column md={4}>
              <TextInput
                value={metadata.completion.service || ''}
                labelText="LLM Service:"
                id="llmservice"
                readOnly
              />
            </Column>
            <Column md={4}>
              <TextInput
                value={metadata.completion.version || ''}
                labelText="LLM Version:"
                id="llmversion"
                readOnly
              />
            </Column>
          </Row>

          <Row>
            <Column md={4}>
              <TextInput
                value={metadata.ocr.service || ''}
                labelText="OCR Service:"
                id="ocrservice"
                readOnly
              />
            </Column>
            <Column md={4}>
              <TextInput
                value={metadata.ocr.version || ''}
                labelText="OCR Version:"
                id="ocrversion"
                readOnly
              />
            </Column>
          </Row>

          <Row>
            <Column md={8}>
              <TextInput
                value={metadata.completion.prompt || ''}
                labelText="Version Prompt:"
                id="versionprompt"
                readOnly
              />
            </Column>
          </Row>
        </Grid>
      </Modal>
    </>
  );
}

function searchParagraphs(
  paragraphs: NonNullable<OCRExtractionResponse['content']['paragraphs']>,
  searchString: string
) {
  if (!searchString) return paragraphs;
  const stringMatch = (str: string) =>
    searchString !== '' &&
    str.toLowerCase().includes(searchString.toLowerCase());

  function highlightString(str: string): string {
    if (searchString === '') return str;
    const regex = new RegExp(`(${searchString})`, 'gi');
    return str.replace(regex, `<span class="${styles.highlight}">$1</span>`);
  }

  return paragraphs
    .map((el) => {
      return stringMatch(el.content)
        ? { ...el, content: highlightString(el.content) }
        : null;
    })
    .filter(
      (
        el
      ): el is NonNullable<
        OCRExtractionResponse['content']['paragraphs']
      >[number] => el !== null
    );
}

const EventsContent = ({ provision }: { provision: ProvisionResponse }) => {
  return (
    <EventsPanel
      header={''}
      title="Provision"
      helperText="Context allows you to share information about a provision."
      eventsHref={provision.resources.events.href}
      commentsHref={provision.resources.comments.href}
    />
  );
};

/**
 * A provision trigger's success flag and error. Each new attempt resets both,
 * so neither toast outlives the attempt that raised it.
 * @return {object} The flag and the error, their setters, and `reset`.
 */
function useProvisionToasts() {
  const [showSuccess, setShowSuccess] = useState(false);
  const [provisionError, setProvisionError] = useState<string | null>(null);
  return {
    showSuccess,
    setShowSuccess,
    provisionError,
    setProvisionError,
    reset: () => {
      setProvisionError(null);
      setShowSuccess(false);
    },
  };
}

type ProvisionToastsState = ReturnType<typeof useProvisionToasts>;

/**
 * Shows the toasts of useProvisionToasts. Carbon calls onClose on the success
 * toast's auto-dismiss too, so its flag resets either way.
 * @param {object} props - The toasts' state.
 * @return {JSX.Element} The toasts that are up.
 */
function ProvisionToasts({ toasts }: { toasts: ProvisionToastsState }) {
  return (
    <>
      {toasts.showSuccess && (
        <SuccessToast
          message="Document Provisioned"
          subtitle="You have successfully provisioned the document"
          onClose={() => {
            toasts.setShowSuccess(false);
            return true;
          }}
        />
      )}
      {toasts.provisionError && (
        <StandingErrorToast
          message={toasts.provisionError}
          onClose={() => toasts.setProvisionError(null)}
        />
      )}
    </>
  );
}
